import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar } from 'lucide-react';

const ETI_LOGO = 'https://customer-assets.emergentagent.com/job_4e0bdddc-c844-4374-a91a-dfbddecb14b1/artifacts/4ane8ulw_eti%20.png';

const Login = () => {
  // Available sessions
  const defaultSessions = [
    { value: '2024', label: '2024-2025' },
    { value: '2025', label: '2025-2026' },
    { value: '2026', label: '2026-2027' }
  ];
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    session: '2026', // Current session
  });
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState(defaultSessions);
  const [currentSession, setCurrentSession] = useState('2026');
  const navigate = useNavigate();

  // No need to fetch - only current session (2026-2027) is available for login

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login({
        username: formData.email,
        password: formData.password,
        session: formData.session,
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('session', response.data.session);
      localStorage.setItem('show_reminders', '1');
      toast.success(`Welcome back! Session: ${formData.session}-${parseInt(formData.session) + 1}`);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const getSessionLabel = (value) => {
    const session = sessions.find(s => s.value === value);
    return session ? session.label : value;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img src={ETI_LOGO} alt="ETI Educom" className="h-20 object-contain" />
          </div>
          <CardTitle className="text-xl font-bold text-slate-900">Branch Management System</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="session" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Academic Session
              </Label>
              <Select
                value={formData.session}
                onValueChange={(value) => setFormData({ ...formData, session: value })}
              >
                <SelectTrigger data-testid="session-select" className="w-full">
                  <SelectValue placeholder="Select session" />
                </SelectTrigger>
                <SelectContent>
                  {sessions.map((session) => (
                    <SelectItem key={session.value} value={session.value}>
                      {session.label}
                      {session.value === currentSession && (
                        <span className="ml-2 text-xs text-green-600 font-medium">(Current)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {formData.session ? (
                  <>Session: April {formData.session} - March {parseInt(formData.session) + 1}</>
                ) : (
                  <>Please select a session</>
                )}
              </p>
            </div>

            <Button
              type="submit"
              data-testid="submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-center pt-2">
              <a href="/student/login" className="text-sm text-emerald-700 hover:text-emerald-800 font-medium" data-testid="student-portal-link">
                Are you a student? Sign in to the Student Portal →
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
